// Credits per persoon (W6 ronde 4).
//
// Per persoon per kredietsoort "gebruikt van maximum" met het stoplicht van de
// server. Het woord voor een soort komt uit KREDIET_LABEL (woorden.ts): zo heet
// "gesprek" hier hetzelfde als in de workspace en op de wachtrij.
//
// Geen maximum (een beheerder zonder rol) = geen kleur: de server zet de stand
// dan op "leeg", want er is niets om het gebruik tegen af te zetten. Dan staat
// er "geen maximum" in woorden, geen grijs bolletje dat "niet gemeten" zegt.
//
// De rol staat met het woord van de server (`rolLabel`, uit rollen.yaml), nooit
// met de code; zonder rol stuurt de server null en staat er een reden.
//
// De status (alleen als die niet "actief" is) is dezelfde pil als op het vlak
// Gebruikers, met dezelfde uitleg: "uitgenodigd" betekent op beide schermen
// hetzelfde.
//
// Het chatbedrag telt de server over deze kalendermaand; de kop zegt dat, zodat
// niemand het voor een totaal over alle tijd aanziet.
//
// W6 ronde 7: de regel per soort (`SoortRegel`) en de open kredietverzoeken
// (`OpenVerzoeken`) staan ook op het tabblad Gebruik van Gebruikers &
// engagement. Daarom geëxporteerd: één regel, zodat "geen maximum" en
// "persoonlijk maximum" op beide schermen hetzelfde zeggen.

import { Card, ReasonBadge } from "../../components/ui";
import { EmptyReason } from "../../components/Empty";
import type { KredietPersoon, KredietSoort } from "../../contracts";
import { color, weight } from "../../tokens";
import { Blok, getal, noot } from "./BronnenStukken";
import { StatusPil } from "./StatusPil";
import { Bedrag, Tabel, cel, klein, kopCel } from "./KostenStukken";
import { Stoplicht } from "./Stoplicht";
import { geenRolReden, kredietLabel } from "./woorden";

export function CreditsPerPersoon({ credits }: { credits: readonly KredietPersoon[] }) {
  return (
    <Blok titel="Credits per persoon">
      {credits.length === 0 ? (
        <Card style={{ padding: 18 }}>
          <EmptyReason reason="er staat nog niemand in de gebruikerslijst" />
        </Card>
      ) : (
        <Tabel label="Credits per persoon" minBreedte={780}>
          <thead>
            <tr>
              <th style={kopCel()}>Naam</th>
              <th style={kopCel()}>Rol</th>
              <th style={kopCel()}>Krediet</th>
              <th style={kopCel()}>Kredietverzoeken</th>
              <th style={kopCel(true)}>Chat deze maand (echt)</th>
            </tr>
          </thead>
          <tbody>
            {credits.map((p) => (
              <PersoonRij key={p.id} persoon={p} />
            ))}
          </tbody>
        </Tabel>
      )}
      <p style={noot}>
        Krediet = gebruikt van het maximum per soort. Chat deze maand (echt) = wat OpenRouter
        deze kalendermaand voor de chatvragen van deze persoon meestuurde.
      </p>
    </Blok>
  );
}

function PersoonRij({ persoon: p }: { persoon: KredietPersoon }) {
  return (
    <tr data-persoon={p.id}>
      <td style={cel()}>
        <div style={{ color: color.ink, fontWeight: weight.medium }}>{p.naam}</div>
        <div style={klein}>{p.email}</div>
        {p.status !== "actief" && (
          <div style={{ marginTop: 4 }}>
            <StatusPil status={p.status} />
          </div>
        )}
      </td>
      <td style={cel()}>
        {/* Geen rol is geldig zolang je beheerder bent (W9-besluit B3): dan een
            reden, geen streepje — zelfde woorden als het vlak Gebruikers. De
            badge alleen náást een rol: zonder rol zegt "beheert alleen" het al. */}
        {p.rolLabel ?? <EmptyReason reason={geenRolReden(p.isAdmin)} />}
        {p.isAdmin && p.rolLabel && (
          <div style={{ marginTop: 4 }}>
            <ReasonBadge text="beheerder" title="Mag uitnodigen en de wachtrij beslissen." />
          </div>
        )}
      </td>
      <td style={cel()}>
        {p.soorten.length === 0 ? (
          <EmptyReason reason="geen krediet en niets gebruikt" />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {p.soorten.map((s) => (
              <SoortRegel key={s.soort} soort={s} />
            ))}
          </div>
        )}
      </td>
      <td style={cel()}>
        <OpenVerzoeken n={p.openKredietverzoeken} />
      </td>
      <td style={cel(true)}>
        <Bedrag n={p.gesprekkenUsd} />
      </td>
    </tr>
  );
}

/** Hoeveel kredietverzoeken van deze persoon op de wachtrij staan. */
export function OpenVerzoeken({ n }: { n: number }) {
  return n > 0 ? (
    <>{`${getal(n)} op de wachtrij`}</>
  ) : (
    <span style={{ color: color.faint }}>niets open</span>
  );
}

export function SoortRegel({ soort: s }: { soort: KredietSoort }) {
  // Een maximum is er als de rol er een geeft, of als deze persoon een eigen
  // maximum heeft — ook een eigen maximum van 0: dan is elk gebruik over de
  // grens en zet de server rood. Alleen zonder beide: geen maximum, geen kleur.
  const heeftMaximum = s.maximum > 0 || s.persoonlijk;
  return (
    <div data-soort={s.soort} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
      {heeftMaximum && <Stoplicht stand={s.stand} />}
      <span style={{ color: color.ink }}>
        {heeftMaximum
          ? `${kredietLabel(s.soort)}: ${getal(s.gebruikt)} van ${getal(s.maximum)}`
          : `${kredietLabel(s.soort)}: ${getal(s.gebruikt)} gebruikt · geen maximum`}
      </span>
      {s.persoonlijk && (
        <ReasonBadge
          text="persoonlijk maximum"
          tone="blue"
          title="Voor deze persoon apart ingesteld, los van de rol."
        />
      )}
      {s.concept && (
        <ReasonBadge
          text="concept"
          tone="amber"
          title="Dit maximum is een voorstel; de owner stelt het vast."
        />
      )}
    </div>
  );
}
